import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { downloadPdf as downloadPdfUtil, openPdfInBrowser } from '../utils/pdfUtils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Plus, PencilSimple, Trash, Export, FileXls, FilePdf, UploadSimple, DownloadSimple,
  ShieldCheck, ShieldWarning, Warning, CurrencyEur, CreditCard, Eye, CaretDown, CaretRight,
} from '@phosphor-icons/react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import DatePickerDMY from '../components/DatePickerDMY';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const formatDateDMY = (dateStr) => {
  if (!dateStr) return '';
  const parts = dateStr.split('-');
  if (parts.length !== 3) return dateStr;
  return `${parts[2]}/${parts[1]}/${parts[0]}`;
};

export default function ElevatorPage() {
  const { t } = useLanguage();
  const { isAdmin } = useAuth();
  const [contracts, setContracts] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [elevatorCompanies, setElevatorCompanies] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterBuilding, setFilterBuilding] = useState('all');
  const [filterCompany, setFilterCompany] = useState('all');
  const [expandedBuildings, setExpandedBuildings] = useState({});
  const fileInputRef = useRef(null);
  const [uploadingContractId, setUploadingContractId] = useState(null);
  const invoiceFileRef = useRef(null);
  const [uploadingExpenseId, setUploadingExpenseId] = useState(null);

  // Contract dialog
  const [contractDialogOpen, setContractDialogOpen] = useState(false);
  const [editingContract, setEditingContract] = useState(null);
  const [contractForm, setContractForm] = useState({
    building_id: '', company_name: '', company_id: '', contact_phone: '', contact_email: '',
    contract_start_date: '', contract_end_date: '', notes: ''
  });
  const [addingContractCompany, setAddingContractCompany] = useState(false);
  const [newContractCompanyName, setNewContractCompanyName] = useState('');

  // Expense dialog
  const [expenseDialogOpen, setExpenseDialogOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [expenseForm, setExpenseForm] = useState({
    building_id: '', company: '', contract_id: '', date: new Date().toISOString().split('T')[0],
    invoice_number: '', invoice_amount: 0, maintenance_notes: '', extra_charges: [],
  });
  const [addingNewCompany, setAddingNewCompany] = useState(false);
  const [newCompanyName, setNewCompanyName] = useState('');

  // Payment dialogs
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [viewPaymentsDialogOpen, setViewPaymentsDialogOpen] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [paymentForm, setPaymentForm] = useState({ amount: '', date: new Date().toISOString().split('T')[0], notes: '' });
  const [expensePayments, setExpensePayments] = useState([]);
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [deletePaymentDialogOpen, setDeletePaymentDialogOpen] = useState(false);
  const [deletingPayment, setDeletingPayment] = useState(null);

  // Delete
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingItem, setDeletingItem] = useState(null);
  const [deleteType, setDeleteType] = useState('');

  // PDF Statement
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
  const [pdfBuilding, setPdfBuilding] = useState('');
  const [pdfDateFrom, setPdfDateFrom] = useState('');
  const [pdfDateTo, setPdfDateTo] = useState('');

  const handleExportPdf = () => {
    if (!pdfBuilding) { toast.error(t('selectBuilding')); return; }
    const params = { building_id: pdfBuilding };
    if (pdfDateFrom) params.date_from = pdfDateFrom;
    if (pdfDateTo) params.date_to = pdfDateTo;
    downloadPdfUtil('/elevator/expenses/statement-pdf', params);
    setPdfDialogOpen(false);
  };
  useEffect(() => { fetchData(); }, [filterBuilding]);

  const fetchData = async () => {
    try {
      const bQs = filterBuilding !== 'all' ? `?building_id=${filterBuilding}` : '';
      const [contractsRes, expensesRes, buildingsRes, summaryRes, companiesRes] = await Promise.all([
        axios.get(`${API}/elevator/contracts${bQs}`),
        axios.get(`${API}/elevator/expenses${bQs}`),
        axios.get(`${API}/buildings`),
        axios.get(`${API}/elevator/expenses/summary${bQs}`),
        axios.get(`${API}/elevator/companies`),
      ]);
      setContracts(contractsRes.data);
      setExpenses(expensesRes.data);
      setBuildings(buildingsRes.data);
      setSummary(summaryRes.data);
      setElevatorCompanies(companiesRes.data);
      // Auto-expand all buildings
      const expanded = {};
      const bids = [...new Set(expensesRes.data.map(e => e.building_id))];
      bids.forEach(id => { expanded[id] = true; });
      setExpandedBuildings(prev => ({ ...prev, ...expanded }));
    } catch {
      toast.error(t('errorOccurred'));
    } finally {
      setLoading(false);
    }
  };

  // Derived: group expenses by building
  const filteredExpenses = filterCompany !== 'all'
    ? expenses.filter(e => e.company === filterCompany)
    : expenses;

  const expensesByBuilding = {};
  filteredExpenses.forEach(e => {
    const bid = e.building_id;
    if (!expensesByBuilding[bid]) {
      expensesByBuilding[bid] = { building_name: e.building_name, byCompany: {}, total: 0, paid: 0 };
    }
    const ck = e.company || '-';
    if (!expensesByBuilding[bid].byCompany[ck]) {
      expensesByBuilding[bid].byCompany[ck] = { expenses: [], total: 0, paid: 0 };
    }
    expensesByBuilding[bid].byCompany[ck].expenses.push(e);
    expensesByBuilding[bid].byCompany[ck].total += e.invoice_amount || 0;
    expensesByBuilding[bid].byCompany[ck].paid += e.total_paid || 0;
    expensesByBuilding[bid].total += e.invoice_amount || 0;
    expensesByBuilding[bid].paid += e.total_paid || 0;
  });

  // Derived: flatten all extra charges from all filtered expenses
  const allExtraCharges = filteredExpenses.flatMap(e =>
    (e.extra_charges || []).filter(ec => ec.description).map(ec => ({
      date: e.date,
      description: ec.description,
      amount: ec.amount || 0,
      source_building: e.building_name,
      source_company: e.company || '',
      source_invoice: e.invoice_number || '',
      expense_id: e.expense_id,
    }))
  );
  const totalExtraChargesAmount = allExtraCharges.reduce((s, ec) => s + ec.amount, 0);
  const totalMonthlyChargesAmount = filteredExpenses.reduce((s, e) => s + (e.invoice_amount || 0), 0);

  // Get building's default company from contracts
  const getBuildingCompany = (buildingId) => {
    const contract = contracts.find(c => c.building_id === buildingId);
    return contract ? contract.company_name : '';
  };

  // All unique companies from expenses
  const uniqueCompanies = [...new Set(expenses.map(e => e.company).filter(Boolean))];

  // ==================== CONTRACT CRUD ====================
  const handleContractSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingContract) {
        await axios.put(`${API}/elevator/contracts/${editingContract.contract_id}`, {
          company_name: contractForm.company_name, company_id: contractForm.company_id,
          contact_phone: contractForm.contact_phone, contact_email: contractForm.contact_email,
          contract_start_date: contractForm.contract_start_date,
          contract_end_date: contractForm.contract_end_date, notes: contractForm.notes,
        });
      } else {
        await axios.post(`${API}/elevator/contracts`, contractForm);
      }
      // Update company phone/email back to company record
      if (contractForm.company_id && (contractForm.contact_phone || contractForm.contact_email)) {
        await axios.put(`${API}/elevator/companies/${contractForm.company_id}`, {
          phone: contractForm.contact_phone, email: contractForm.contact_email,
        }).catch(() => {});
      }
      toast.success(t('savedSuccess'));
      setContractDialogOpen(false);
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || t('errorOccurred')); }
  };

  const openContractDialog = (contract = null) => {
    setEditingContract(contract);
    setAddingContractCompany(false);
    if (contract) {
      const matchedCompany = elevatorCompanies.find(c => c.name === contract.company_name);
      setContractForm({
        building_id: contract.building_id, company_name: contract.company_name,
        company_id: matchedCompany?.company_id || '',
        contact_phone: contract.contact_phone || '', contact_email: contract.contact_email || '',
        contract_start_date: contract.contract_start_date, contract_end_date: contract.contract_end_date,
        notes: contract.notes || '',
      });
    } else {
      setContractForm({ building_id: '', company_name: '', company_id: '', contact_phone: '', contact_email: '', contract_start_date: '', contract_end_date: '', notes: '' });
    }
    setContractDialogOpen(true);
  };

  const handleContractCompanySelect = (val) => {
    if (val === '_add_new') {
      setAddingContractCompany(true);
      setNewContractCompanyName('');
    } else {
      const company = elevatorCompanies.find(c => c.company_id === val);
      if (company) {
        setContractForm({
          ...contractForm,
          company_name: company.name,
          company_id: company.company_id,
          contact_phone: company.phone || contractForm.contact_phone,
          contact_email: company.email || contractForm.contact_email,
        });
      }
    }
  };

  const handleAddContractCompany = async () => {
    if (!newContractCompanyName.trim()) return;
    try {
      const res = await axios.post(`${API}/elevator/companies`, { name: newContractCompanyName.trim() });
      const companiesRes = await axios.get(`${API}/elevator/companies`);
      setElevatorCompanies(companiesRes.data);
      const newCompany = companiesRes.data.find(c => c.name === newContractCompanyName.trim());
      if (newCompany) {
        setContractForm({ ...contractForm, company_name: newCompany.name, company_id: newCompany.company_id });
      }
      setAddingContractCompany(false);
      toast.success(t('savedSuccess'));
    } catch (error) { toast.error(error.response?.data?.detail || t('errorOccurred')); }
  };

  // ==================== EXPENSE CRUD ====================
  const handleExpenseSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        company: expenseForm.company, date: expenseForm.date,
        invoice_number: expenseForm.invoice_number,
        maintenance_notes: expenseForm.maintenance_notes,
        extra_charges: expenseForm.extra_charges.filter(ec => ec.description),
        contract_id: expenseForm.contract_id || null,
      };
      if (editingExpense) {
        if ((editingExpense.payment_count || 0) === 0) {
          payload.invoice_amount = parseFloat(expenseForm.invoice_amount) || 0;
        }
        await axios.put(`${API}/elevator/expenses/${editingExpense.expense_id}`, payload);
      } else {
        payload.building_id = expenseForm.building_id;
        payload.invoice_amount = parseFloat(expenseForm.invoice_amount) || 0;
        await axios.post(`${API}/elevator/expenses`, payload);
      }
      toast.success(t('savedSuccess'));
      setExpenseDialogOpen(false);
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || t('errorOccurred')); }
  };

  const openExpenseDialog = (expense = null) => {
    setAddingNewCompany(false);
    setNewCompanyName('');
    if (expense) {
      setEditingExpense(expense);
      setExpenseForm({
        building_id: expense.building_id, company: expense.company || '',
        contract_id: expense.contract_id || '', date: expense.date,
        invoice_number: expense.invoice_number || '', invoice_amount: expense.invoice_amount,
        maintenance_notes: expense.maintenance_notes || '',
        extra_charges: expense.extra_charges || [],
      });
    } else {
      const defaultBldg = filterBuilding !== 'all' ? filterBuilding : '';
      const defaultCompany = defaultBldg ? getBuildingCompany(defaultBldg) : '';
      setEditingExpense(null);
      setExpenseForm({
        building_id: defaultBldg, company: defaultCompany, contract_id: '',
        date: new Date().toISOString().split('T')[0], invoice_number: '',
        invoice_amount: 0, maintenance_notes: '', extra_charges: [],
      });
    }
    setExpenseDialogOpen(true);
  };

  const handleBuildingChangeInExpense = (bid) => {
    const company = getBuildingCompany(bid);
    setExpenseForm({ ...expenseForm, building_id: bid, company });
  };

  const handleInlineAddCompany = async () => {
    if (!newCompanyName.trim()) return;
    try {
      await axios.post(`${API}/elevator/companies`, { name: newCompanyName.trim() });
      setExpenseForm({ ...expenseForm, company: newCompanyName.trim() });
      setAddingNewCompany(false);
      setNewCompanyName('');
      const res = await axios.get(`${API}/elevator/companies`);
      setElevatorCompanies(res.data);
      toast.success(t('savedSuccess'));
    } catch (error) { toast.error(error.response?.data?.detail || t('errorOccurred')); }
  };

  // ==================== PAYMENT CRUD ====================
  const openAddPayment = (expense) => {
    setSelectedExpense(expense);
    setPaymentForm({ amount: '', date: new Date().toISOString().split('T')[0], notes: '' });
    setPaymentDialogOpen(true);
  };

  const handleAddPayment = async (e) => {
    e.preventDefault();
    const amount = parseFloat(paymentForm.amount);
    if (!amount || amount <= 0) { toast.error(t('amount') + ' > 0'); return; }
    try {
      await axios.post(`${API}/elevator/expenses/${selectedExpense.expense_id}/payments`, {
        amount, date: paymentForm.date, notes: paymentForm.notes,
      });
      toast.success(t('savedSuccess'));
      setPaymentDialogOpen(false);
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || t('errorOccurred')); }
  };

  const openViewPayments = async (expense) => {
    setSelectedExpense(expense);
    setViewPaymentsDialogOpen(true);
    setLoadingPayments(true);
    try {
      const res = await axios.get(`${API}/elevator/expenses/${expense.expense_id}/payments`);
      setExpensePayments(res.data);
    } catch { toast.error(t('errorOccurred')); }
    finally { setLoadingPayments(false); }
  };

  const handleDeletePayment = async () => {
    if (!deletingPayment) return;
    try {
      await axios.delete(`${API}/elevator/expense-payments/${deletingPayment.payment_id}`);
      toast.success(t('deletedSuccess'));
      setDeletePaymentDialogOpen(false);
      setDeletingPayment(null);
      if (selectedExpense) {
        const res = await axios.get(`${API}/elevator/expenses/${selectedExpense.expense_id}/payments`);
        setExpensePayments(res.data);
      }
      fetchData();
    } catch { toast.error(t('errorOccurred')); }
  };

  // ==================== DELETE ====================
  const handleDelete = async () => {
    try {
      if (deleteType === 'contract') {
        await axios.delete(`${API}/elevator/contracts/${deletingItem.contract_id}`);
      } else {
        await axios.delete(`${API}/elevator/expenses/${deletingItem.expense_id}`);
      }
      toast.success(t('deletedSuccess'));
      setDeleteDialogOpen(false);
      fetchData();
    } catch { toast.error(t('errorOccurred')); }
  };

  // ==================== FILE UPLOAD ====================
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !uploadingContractId) return;
    const fd = new FormData(); fd.append('file', file);
    try {
      await axios.post(`${API}/elevator/contracts/${uploadingContractId}/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(t('savedSuccess')); fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || t('errorOccurred')); }
    setUploadingContractId(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleInvoiceFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !uploadingExpenseId) return;
    const fd = new FormData(); fd.append('file', file);
    try {
      await axios.post(`${API}/elevator/expenses/${uploadingExpenseId}/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(t('savedSuccess')); fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || t('errorOccurred')); }
    setUploadingExpenseId(null);
    if (invoiceFileRef.current) invoiceFileRef.current.value = '';
  };

  const getStatusIcon = (status) => {
    if (status === 'active') return <ShieldCheck size={16} weight="fill" className="text-emerald-600" />;
    if (status === 'expiring_soon') return <ShieldWarning size={16} weight="fill" className="text-amber-600" />;
    if (status === 'expired') return <Warning size={16} weight="fill" className="text-red-600" />;
    return null;
  };
  const getStatusColor = (s) => s === 'active' ? 'text-emerald-600' : s === 'expiring_soon' ? 'text-amber-600' : 'text-red-600';

  const exportToExcel = () => {
    const data = filteredExpenses.map(e => ({
      [t('buildings')]: e.building_name, [t('elevatorCompany')]: e.company || '',
      [t('expenseDate')]: e.date, [t('invoiceNumber')]: e.invoice_number || '',
      [t('invoiceAmount')]: e.invoice_amount, [t('paidAmount')]: e.total_paid || 0,
      [t('expenseRemainingBalance')]: e.remaining_balance || e.invoice_amount,
      [t('status')]: e.status === 'paid' ? t('paid') : e.status === 'partially_paid' ? t('partiallyPaid') : t('unpaidExpenses'),
      [t('maintenanceNotes')]: e.maintenance_notes || '',
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Elevator Expenses');
    XLSX.writeFile(wb, 'elevator_expenses.xlsx');
  };

  const toggleBuilding = (bid) => setExpandedBuildings(prev => ({ ...prev, [bid]: !prev[bid] }));

  if (loading) return <Layout><div className="flex items-center justify-center h-64"><div className="text-zinc-500">{t('loading')}</div></div></Layout>;

  return (
    <Layout>
      <div data-testid="elevator-page">
        <input type="file" ref={fileInputRef} accept=".pdf" className="hidden" onChange={handleFileUpload} />
        <input type="file" ref={invoiceFileRef} accept=".pdf,.png,.jpg,.jpeg,.webp" className="hidden" onChange={handleInvoiceFileUpload} />

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6">
          <div className="bg-white border border-zinc-200 p-4" data-testid="total-elevator-cost-card">
            <p className="text-sm text-zinc-500">{t('totalElevatorCost')}</p>
            <p className="text-2xl font-bold font-mono mt-1">&euro;{(summary?.total_expenses || 0).toFixed(2)}</p>
            <p className="text-xs text-zinc-400 mt-1">{summary?.expense_count || 0} invoices</p>
          </div>
          <div className="bg-white border border-emerald-200 p-4" data-testid="elevator-paid-card">
            <p className="text-sm text-emerald-600">{t('paidExpenses')}</p>
            <p className="text-2xl font-bold font-mono text-emerald-700 mt-1">&euro;{(summary?.total_paid || 0).toFixed(2)}</p>
          </div>
          <div className="bg-white border border-red-200 p-4" data-testid="elevator-unpaid-card">
            <p className="text-sm text-red-600">{t('unpaidExpenses')}</p>
            <p className="text-2xl font-bold font-mono text-red-700 mt-1">&euro;{(summary?.total_unpaid || 0).toFixed(2)}</p>
          </div>
          <div className="bg-white border border-blue-200 p-4" data-testid="contracts-card">
            <p className="text-sm text-blue-600">{t('contracts')}</p>
            <p className="text-2xl font-bold font-mono text-blue-700 mt-1">{contracts.length}</p>
          </div>
        </div>

        {/* Filters & Actions */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <Select value={filterBuilding} onValueChange={setFilterBuilding}>
            <SelectTrigger className="w-[200px] rounded-none" data-testid="building-filter"><SelectValue placeholder={t('buildings')} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('all')} {t('buildings')}</SelectItem>
              {buildings.map(b => <SelectItem key={b.building_id} value={b.building_id}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterCompany} onValueChange={setFilterCompany}>
            <SelectTrigger className="w-[200px] rounded-none" data-testid="company-filter"><SelectValue placeholder={t('elevatorCompany')} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('all')} {t('elevatorCompanies')}</SelectItem>
              {uniqueCompanies.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="flex gap-2 ml-auto">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="border-zinc-200 rounded-none" data-testid="export-btn"><Export size={18} className="mr-2" />{t('export')}</Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={exportToExcel}><FileXls size={18} className="mr-2" />Excel</DropdownMenuItem>
                <DropdownMenuItem onClick={() => { setPdfBuilding(filterBuilding !== 'all' ? filterBuilding : ''); setPdfDateFrom(''); setPdfDateTo(''); setPdfDialogOpen(true); }}><FilePdf size={18} className="mr-2" />{t('pdfStatement')}</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button onClick={() => openContractDialog()} className="bg-zinc-950 rounded-none" data-testid="add-contract-btn"><Plus size={18} className="mr-2" />{t('addContract')}</Button>
            <Button onClick={() => openExpenseDialog()} variant="outline" className="border-zinc-200 rounded-none" data-testid="add-elevator-expense-btn"><Plus size={18} className="mr-2" />{t('addElevatorExpense')}</Button>
          </div>
        </div>

        {/* ============ EXTRA CHARGES SUMMARY (plain text) ============ */}
        {allExtraCharges.length > 0 && (
          <p className="text-xs text-zinc-500" data-testid="extra-charges-summary">
            {t('extraCharges')}: {allExtraCharges.length} {allExtraCharges.length === 1 ? 'item' : 'items'} — <span className="font-mono font-semibold text-zinc-700">&euro;{totalExtraChargesAmount.toFixed(2)}</span>
          </p>
        )}

        {/* Contracts Section */}
        <h2 className="text-lg font-bold mb-3" style={{ fontFamily: 'var(--font-heading)' }}>{t('contracts')}</h2>
        <div className="bg-white border border-zinc-200 mb-8 overflow-x-auto">
          {contracts.length === 0 ? (
            <div className="p-12 text-center text-zinc-500">{t('noContracts')}</div>
          ) : (
            <table className="excel-table" data-testid="contracts-table">
              <thead>
                <tr>
                  <th>{t('buildings')}</th><th>{t('maintenanceCompany')}</th>
                  <th>{t('contractStartDate')}</th><th>{t('contractEndDate')}</th>
                  <th>{t('contractStatus')}</th><th>{t('totalElevatorCost')}</th>
                  <th>{t('contractFile')}</th><th className="w-24">{t('edit')}</th>
                </tr>
              </thead>
              <tbody>
                {contracts.map(c => (
                  <tr key={c.contract_id} data-testid={`contract-row-${c.contract_id}`}>
                    <td className="font-medium">{c.building_name}</td>
                    <td><div className="font-medium">{c.company_name}</div>{c.contact_phone && <div className="text-xs text-zinc-500">{c.contact_phone}</div>}</td>
                    <td className="font-mono text-sm">{formatDateDMY(c.contract_start_date)}</td>
                    <td className="font-mono text-sm">{formatDateDMY(c.contract_end_date)}</td>
                    <td>
                      <div className="flex items-center gap-1.5">{getStatusIcon(c.contract_status)}<span className={`text-sm font-medium ${getStatusColor(c.contract_status)}`}>{t(c.contract_status)}</span></div>
                      <div className={`text-xs font-mono mt-0.5 ${getStatusColor(c.contract_status)}`}>{c.days_remaining >= 0 ? `${c.days_remaining} ${t('daysRemaining')}` : `${t('expiredDaysAgo')} ${Math.abs(c.days_remaining)} ${t('days')}`}</div>
                    </td>
                    <td className="font-mono font-semibold">&euro;{(c.total_expenses || 0).toFixed(2)}</td>
                    <td>
                      <div className="flex gap-1">
                        <button onClick={() => { setUploadingContractId(c.contract_id); fileInputRef.current?.click(); }} className="p-2 hover:bg-blue-50 text-blue-600" title={t('uploadContract')}><UploadSimple size={18} /></button>
                        {c.contract_file_url && <button onClick={() => { openPdfInBrowser(`/elevator/contracts/${c.contract_id}/file`, {}).catch(e => toast.error(e?.message || 'Failed to open')); }} className="p-2 hover:bg-emerald-50 text-emerald-600" title={t('downloadContract')}><DownloadSimple size={18} /></button>}
                      </div>
                    </td>
                    <td>
                      <div className="flex gap-1">
                        <button onClick={() => openContractDialog(c)} className="p-2 hover:bg-zinc-100" title={t('edit')}><PencilSimple size={18} /></button>
                        {isAdmin && <button onClick={() => { setDeletingItem(c); setDeleteType('contract'); setDeleteDialogOpen(true); }} className="p-2 hover:bg-red-50 text-red-600" title={t('delete')}><Trash size={18} /></button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Monthly Charges Grouped by Building */}
        <div className="flex items-center gap-3 mb-3">
          <h2 className="text-lg font-bold" style={{ fontFamily: 'var(--font-heading)' }}>{t('monthlyCharges')}</h2>
          <span className="px-2 py-0.5 text-[10px] font-semibold uppercase border border-blue-200 bg-blue-50 text-blue-700">{t('recurringMonthly')}</span>
        </div>
        {filteredExpenses.length === 0 ? (
          <div className="bg-white border border-zinc-200 p-12 text-center text-zinc-500">{t('noElevatorExpenses')}</div>
        ) : (
          <div className="space-y-4" data-testid="grouped-elevator-expenses">
            {Object.entries(expensesByBuilding).map(([bid, group]) => {
              const isExpanded = expandedBuildings[bid] !== false;
              const buildingContract = contracts.find(c => c.building_id === bid);
              return (
                <div key={bid} className="bg-white border border-zinc-200" data-testid={`building-group-${bid}`}>
                  <button onClick={() => toggleBuilding(bid)} className="w-full flex items-center gap-3 px-5 py-4 hover:bg-zinc-50 transition-colors text-left">
                    <div className="flex-shrink-0 text-zinc-400">{isExpanded ? <CaretDown size={18} weight="bold" /> : <CaretRight size={18} weight="bold" />}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3">
                        <span className="font-semibold">{group.building_name}</span>
                        {buildingContract && <span className="text-xs text-zinc-500 px-2 py-0.5 bg-zinc-100 border border-zinc-200">{buildingContract.company_name}</span>}
                      </div>
                      <div className="flex gap-4 mt-1 text-xs font-mono">
                        <span className="text-zinc-500">{t('total')}: &euro;{group.total.toFixed(2)}</span>
                        <span className="text-emerald-600">{t('paid')}: &euro;{group.paid.toFixed(2)}</span>
                        <span className="text-red-600">{t('unpaidExpenses')}: &euro;{(group.total - group.paid).toFixed(2)}</span>
                      </div>
                    </div>
                    <span className="text-sm text-zinc-400">{Object.values(group.byCompany).reduce((s, c) => s + c.expenses.length, 0)} invoices</span>
                  </button>
                  {isExpanded && (
                    <div className="border-t border-zinc-200">
                      {Object.entries(group.byCompany).map(([companyName, cg]) => (
                        <div key={companyName} className="border-b border-zinc-100 last:border-b-0">
                          <div className="px-5 py-2 bg-blue-50/40 border-b border-zinc-100 flex items-center justify-between" data-testid={`company-header-${bid}-${companyName}`}>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-semibold text-zinc-700">{companyName}</span>
                              <span className="text-xs text-zinc-400">({cg.expenses.length} invoices)</span>
                            </div>
                            <div className="flex gap-4 text-xs font-mono">
                              <span className="text-zinc-500">{t('total')}: &euro;{cg.total.toFixed(2)}</span>
                              <span className="text-emerald-600">{t('paid')}: &euro;{cg.paid.toFixed(2)}</span>
                              <span className="text-red-600">{t('unpaidExpenses')}: &euro;{(cg.total - cg.paid).toFixed(2)}</span>
                            </div>
                          </div>
                          <div className="overflow-x-auto">
                            <table className="excel-table" data-testid={`building-table-${bid}-${companyName}`}>
                              <thead>
                                <tr className="bg-zinc-50">
                                  <th className="text-xs">{t('expenseDate')}</th>
                                  <th className="text-xs">{t('invoiceNumber')}</th>
                                  <th className="text-xs text-right">{t('invoiceAmount')}</th>
                                  <th className="text-xs text-right">{t('paidAmount')}</th>
                                  <th className="text-xs text-right">{t('expenseRemainingBalance')}</th>
                                  <th className="text-xs">{t('status')}</th>
                                  <th className="text-xs min-w-[180px]">{t('notes')}</th>
                                  <th className="text-xs w-40">{t('edit')}</th>
                                </tr>
                              </thead>
                              <tbody>
                                {cg.expenses.map(e => (
                                  <tr key={e.expense_id} data-testid={`elev-exp-row-${e.expense_id}`}>
                                    <td className="font-mono text-sm">{formatDateDMY(e.date)}</td>
                                    <td className="font-mono text-sm">{e.invoice_number || '-'}</td>
                                    <td className="font-mono font-semibold text-right">&euro;{(e.invoice_amount || 0).toFixed(2)}</td>
                                    <td className="font-mono text-right text-emerald-700">&euro;{(e.total_paid || 0).toFixed(2)}</td>
                                    <td className={`font-mono font-semibold text-right ${(e.remaining_balance || 0) > 0 ? 'text-red-600' : 'text-emerald-600'}`}>&euro;{(e.remaining_balance ?? e.invoice_amount ?? 0).toFixed(2)}</td>
                                    <td>
                                      {e.status === 'paid' && <span className="inline-flex items-center px-2 py-0.5 text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">{t('paid')}</span>}
                                      {e.status === 'partially_paid' && <span className="inline-flex items-center px-2 py-0.5 text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200">{t('partiallyPaid')}</span>}
                                      {e.status === 'unpaid' && <span className="inline-flex items-center px-2 py-0.5 text-xs font-semibold bg-red-50 text-red-700 border border-red-200">{t('unpaidExpenses')}</span>}
                                    </td>
                                    <td className="text-sm max-w-xs">
                                      {e.maintenance_notes && <div className="text-zinc-700"><span className="text-zinc-400 text-xs">{t('maintenanceNotes')}:</span> {e.maintenance_notes}</div>}
                                      {!e.maintenance_notes && <span className="text-zinc-400">{e.notes || '-'}</span>}
                                    </td>
                                    <td>
                                      <div className="flex gap-1">
                                        <button onClick={() => openAddPayment(e)} className="p-2 hover:bg-blue-50 text-blue-600" title={t('addPayment')} data-testid={`add-pay-${e.expense_id}`}><CreditCard size={18} /></button>
                                        <button onClick={() => openViewPayments(e)} className="p-2 hover:bg-zinc-100 text-zinc-600" title={t('viewPayments')} data-testid={`view-pay-${e.expense_id}`}><Eye size={18} /></button>
                                        <button onClick={() => openExpenseDialog(e)} className="p-2 hover:bg-zinc-100" title={t('edit')} data-testid={`edit-exp-${e.expense_id}`}><PencilSimple size={18} /></button>
                                        {isAdmin && <button onClick={() => { setDeletingItem(e); setDeleteType('expense'); setDeleteDialogOpen(true); }} className="p-2 hover:bg-red-50 text-red-600" title={t('delete')} data-testid={`del-exp-${e.expense_id}`}><Trash size={18} /></button>}
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                              <tfoot>
                                <tr className="bg-zinc-50 font-semibold">
                                  <td colSpan={2} className="text-right text-sm">{t('total')}</td>
                                  <td className="font-mono text-right">&euro;{cg.total.toFixed(2)}</td>
                                  <td className="font-mono text-right text-emerald-700">&euro;{cg.paid.toFixed(2)}</td>
                                  <td className="font-mono text-right text-red-600">&euro;{(cg.total - cg.paid).toFixed(2)}</td>
                                  <td colSpan={3}></td>
                                </tr>
                              </tfoot>
                            </table>
                          </div>
                        </div>
                      ))}
                      {Object.keys(group.byCompany).length > 1 && (
                        <div className="px-5 py-3 bg-zinc-100 border-t border-zinc-200 flex justify-end gap-6 text-sm font-semibold font-mono">
                          <span className="text-zinc-600">{t('buildingTotal')}: &euro;{group.total.toFixed(2)}</span>
                          <span className="text-emerald-700">{t('paid')}: &euro;{group.paid.toFixed(2)}</span>
                          <span className="text-red-600">{t('unpaidExpenses')}: &euro;{(group.total - group.paid).toFixed(2)}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* ============ GRAND TOTALS ============ */}
        {filteredExpenses.length > 0 && (
          <div className="bg-white border-2 border-zinc-300 mt-6 mb-4" data-testid="grand-totals-section">
            <div className="px-5 py-3 bg-zinc-100 border-b border-zinc-200">
              <h3 className="text-xs font-bold uppercase tracking-wider text-zinc-600">{t('statementGrandTotal')}</h3>
            </div>
            <div className="divide-y divide-zinc-100">
              {allExtraCharges.length > 0 && (
                <div className="px-5 py-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-amber-400 rounded-full" />
                    <span className="text-sm font-medium text-zinc-700">{t('totalExtraCharges')}</span>
                    <span className="text-xs text-zinc-400">({allExtraCharges.length})</span>
                  </div>
                  <span className="font-mono font-semibold text-amber-800">&euro;{totalExtraChargesAmount.toFixed(2)}</span>
                </div>
              )}
              <div className="px-5 py-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-blue-400 rounded-full" />
                  <span className="text-sm font-medium text-zinc-700">{t('totalMonthlyCharges')}</span>
                  <span className="text-xs text-zinc-400">({filteredExpenses.length} invoices)</span>
                </div>
                <span className="font-mono font-semibold text-zinc-800">&euro;{totalMonthlyChargesAmount.toFixed(2)}</span>
              </div>
              <div className="px-5 py-4 flex items-center justify-between bg-zinc-50">
                <span className="text-sm font-bold text-zinc-900 uppercase tracking-wider">{t('statementGrandTotal')}</span>
                <span className="font-mono font-bold text-lg text-zinc-900">&euro;{(totalMonthlyChargesAmount + totalExtraChargesAmount).toFixed(2)}</span>
              </div>
            </div>
          </div>
        )}

        {/* ====== CONTRACT DIALOG ====== */}
        <Dialog open={contractDialogOpen} onOpenChange={setContractDialogOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader><DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>{editingContract ? t('editContract') : t('addContract')}</DialogTitle></DialogHeader>
            <form onSubmit={handleContractSubmit} className="space-y-4">
              <div>
                <Label>{t('buildings')}</Label>
                <Select value={contractForm.building_id} onValueChange={(val) => setContractForm({ ...contractForm, building_id: val })} required>
                  <SelectTrigger className="mt-1 rounded-none" data-testid="contract-building-select"><SelectValue placeholder={t('buildings')} /></SelectTrigger>
                  <SelectContent>{buildings.map(b => <SelectItem key={b.building_id} value={b.building_id}>{b.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t('maintenanceCompany')}</Label>
                {!addingContractCompany ? (
                  <Select value={contractForm.company_id} onValueChange={handleContractCompanySelect} required>
                    <SelectTrigger className="mt-1 rounded-none" data-testid="contract-company-select"><SelectValue placeholder={t('selectCompany')} /></SelectTrigger>
                    <SelectContent>
                      {elevatorCompanies.map(c => <SelectItem key={c.company_id} value={c.company_id}>{c.name}{c.phone ? ` (${c.phone})` : ''}</SelectItem>)}
                      <SelectItem value="_add_new">+ {t('addNewCompany')}</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="flex gap-2 mt-1">
                    <Input value={newContractCompanyName} onChange={(e) => setNewContractCompanyName(e.target.value)} className="rounded-none flex-1" placeholder={t('companyName')} data-testid="new-contract-company-input" autoFocus />
                    <Button type="button" size="sm" onClick={handleAddContractCompany} className="bg-zinc-950 rounded-none">{t('save')}</Button>
                    <Button type="button" size="sm" variant="outline" onClick={() => setAddingContractCompany(false)} className="rounded-none">{t('cancel')}</Button>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>{t('contactPhone')}</Label><Input value={contractForm.contact_phone} onChange={(e) => setContractForm({ ...contractForm, contact_phone: e.target.value })} className="mt-1 rounded-none" data-testid="contract-phone-input" /></div>
                <div><Label>{t('contactEmail')}</Label><Input type="email" value={contractForm.contact_email} onChange={(e) => setContractForm({ ...contractForm, contact_email: e.target.value })} className="mt-1 rounded-none" data-testid="contract-email-input" /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>{t('contractStartDate')}</Label><DatePickerDMY value={contractForm.contract_start_date} onChange={v => setContractForm({ ...contractForm, contract_start_date: v })} className="mt-1 font-mono" required data-testid="contract-start-input" /></div>
                <div><Label>{t('contractEndDate')}</Label><DatePickerDMY value={contractForm.contract_end_date} onChange={v => setContractForm({ ...contractForm, contract_end_date: v })} className="mt-1 font-mono" required data-testid="contract-end-input" /></div>
              </div>
              <div><Label>{t('notes')}</Label><Textarea value={contractForm.notes} onChange={(e) => setContractForm({ ...contractForm, notes: e.target.value })} className="mt-1 rounded-none" rows={2} data-testid="contract-notes-input" /></div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setContractDialogOpen(false)} className="rounded-none">{t('cancel')}</Button>
                <Button type="submit" className="bg-zinc-950 rounded-none" data-testid="save-contract-btn">{t('save')}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* ====== EXPENSE DIALOG ====== */}
        <Dialog open={expenseDialogOpen} onOpenChange={setExpenseDialogOpen}>
          <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>{editingExpense ? t('editElevatorExpense') : t('addElevatorExpense')}</DialogTitle></DialogHeader>
            <form onSubmit={handleExpenseSubmit} className="space-y-4">
              <div>
                <Label>{t('buildings')}</Label>
                <Select value={expenseForm.building_id} onValueChange={handleBuildingChangeInExpense} required>
                  <SelectTrigger className="mt-1 rounded-none" data-testid="expense-building-select"><SelectValue placeholder={t('buildings')} /></SelectTrigger>
                  <SelectContent>{buildings.map(b => <SelectItem key={b.building_id} value={b.building_id}>{b.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t('elevatorCompany')} *</Label>
                {!addingNewCompany ? (
                  <Select value={expenseForm.company} onValueChange={(val) => {
                    if (val === '_add_new') { setAddingNewCompany(true); setNewCompanyName(''); }
                    else { setExpenseForm({ ...expenseForm, company: val }); }
                  }}>
                    <SelectTrigger className="mt-1 rounded-none" data-testid="expense-company-select"><SelectValue placeholder={t('elevatorCompany')} /></SelectTrigger>
                    <SelectContent>
                      {elevatorCompanies.map(c => <SelectItem key={c.company_id} value={c.name}>{c.name}</SelectItem>)}
                      {contracts.filter(c => !elevatorCompanies.some(ec => ec.name === c.company_name)).map(c => <SelectItem key={`ct-${c.contract_id}`} value={c.company_name}>{c.company_name}</SelectItem>)}
                      <SelectItem value="_add_new">+ {t('addNewCompany')}</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="flex gap-2 mt-1">
                    <Input value={newCompanyName} onChange={(e) => setNewCompanyName(e.target.value)} className="rounded-none flex-1" placeholder={t('companyName')} data-testid="new-company-input" autoFocus />
                    <Button type="button" size="sm" onClick={handleInlineAddCompany} className="bg-zinc-950 rounded-none">{t('save')}</Button>
                    <Button type="button" size="sm" variant="outline" onClick={() => setAddingNewCompany(false)} className="rounded-none">{t('cancel')}</Button>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>{t('invoiceNumber')}</Label><Input value={expenseForm.invoice_number} onChange={(e) => setExpenseForm({ ...expenseForm, invoice_number: e.target.value })} className="mt-1 rounded-none font-mono" data-testid="invoice-number-input" /></div>
                <div><Label>{t('date')}</Label><DatePickerDMY value={expenseForm.date} onChange={v => setExpenseForm({ ...expenseForm, date: v })} className="mt-1 font-mono" required data-testid="expense-date-input" /></div>
              </div>
              <div>
                <Label>{t('invoiceAmount')} (&euro;)</Label>
                <Input type="number" step="0.01" min="0" value={expenseForm.invoice_amount} onChange={(e) => setExpenseForm({ ...expenseForm, invoice_amount: parseFloat(e.target.value) || 0 })} className="mt-1 rounded-none font-mono" required disabled={editingExpense && (editingExpense.payment_count || 0) > 0} data-testid="expense-amount-input" />
                {editingExpense && (editingExpense.payment_count || 0) > 0 && <p className="text-xs text-zinc-400 mt-1">Amount locked (payments exist)</p>}
              </div>
              <div className="border-t border-zinc-200 pt-4">
                <Label className="text-sm font-semibold">{t('maintenanceNotes')}</Label>
                <Textarea value={expenseForm.maintenance_notes} onChange={(e) => setExpenseForm({ ...expenseForm, maintenance_notes: e.target.value })} className="mt-1 rounded-none resize-y" rows={3} style={{ minHeight: '80px', maxHeight: '50vh' }} placeholder={t('maintenanceNotes') + '...'} data-testid="maintenance-notes-input" />
              </div>
              <div className="border-t border-zinc-200 pt-4">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm font-semibold">{t('extraCharges')}</Label>
                  <Button type="button" variant="outline" size="sm" onClick={() => setExpenseForm({ ...expenseForm, extra_charges: [...expenseForm.extra_charges, { description: '', amount: 0 }] })} className="rounded-none text-xs" data-testid="add-extra-charge-btn"><Plus size={14} className="mr-1" />{t('addExtraCharge')}</Button>
                </div>
                {expenseForm.extra_charges.map((charge, idx) => (
                  <div key={idx} className="flex gap-2 items-center mb-2">
                    <Input value={charge.description} onChange={(e) => { const u = [...expenseForm.extra_charges]; u[idx] = { ...u[idx], description: e.target.value }; setExpenseForm({ ...expenseForm, extra_charges: u }); }} className="rounded-none flex-1" placeholder={t('chargeDescription')} />
                    <Input type="number" step="0.01" min="0" value={charge.amount} onChange={(e) => { const u = [...expenseForm.extra_charges]; u[idx] = { ...u[idx], amount: parseFloat(e.target.value) || 0 }; setExpenseForm({ ...expenseForm, extra_charges: u }); }} className="rounded-none w-28 font-mono" />
                    <button type="button" onClick={() => setExpenseForm({ ...expenseForm, extra_charges: expenseForm.extra_charges.filter((_, i) => i !== idx) })} className="p-1.5 hover:bg-red-50 text-red-500"><Trash size={16} /></button>
                  </div>
                ))}
                {expenseForm.extra_charges.length > 0 && <div className="text-right text-xs font-mono text-zinc-500 pt-1 border-t border-zinc-100">{t('total')}: &euro;{expenseForm.extra_charges.reduce((s, c) => s + (c.amount || 0), 0).toFixed(2)}</div>}
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setExpenseDialogOpen(false)} className="rounded-none">{t('cancel')}</Button>
                <Button type="submit" className="bg-zinc-950 rounded-none" data-testid="save-elevator-expense-btn"><CurrencyEur size={18} className="mr-2" />{t('save')}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* ====== ADD PAYMENT DIALOG ====== */}
        <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader><DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>{t('addPayment')}</DialogTitle></DialogHeader>
            {selectedExpense && (
              <div className="bg-zinc-50 border border-zinc-200 p-3 mb-2 space-y-1">
                <p className="text-sm font-medium">{selectedExpense.building_name} &mdash; {selectedExpense.company}</p>
                <div className="flex justify-between text-sm"><span className="text-zinc-500">{t('invoiceAmount')}:</span><span className="font-mono font-semibold">&euro;{(selectedExpense.invoice_amount || 0).toFixed(2)}</span></div>
                <div className="flex justify-between text-sm"><span className="text-zinc-500">{t('paidAmount')}:</span><span className="font-mono text-emerald-700">&euro;{(selectedExpense.total_paid || 0).toFixed(2)}</span></div>
                <div className="flex justify-between text-sm font-semibold"><span className="text-zinc-500">{t('expenseRemainingBalance')}:</span><span className={`font-mono ${(selectedExpense.remaining_balance || 0) > 0 ? 'text-red-600' : 'text-emerald-600'}`}>&euro;{(selectedExpense.remaining_balance ?? selectedExpense.invoice_amount ?? 0).toFixed(2)}</span></div>
              </div>
            )}
            <form onSubmit={handleAddPayment} className="space-y-4">
              <div><Label>{t('amount')} (&euro;)</Label><Input type="number" step="0.01" min="0.01" value={paymentForm.amount} onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })} className="mt-1 rounded-none font-mono" required placeholder="0.00" data-testid="payment-amount-input" /></div>
              <div><Label>{t('date')}</Label><DatePickerDMY value={paymentForm.date} onChange={v => setPaymentForm({ ...paymentForm, date: v })} className="mt-1 font-mono" required data-testid="payment-date-input" /></div>
              <div><Label>{t('notes')}</Label><Textarea value={paymentForm.notes} onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })} className="mt-1 rounded-none resize-y" rows={3} style={{ minHeight: '80px', maxHeight: '50vh' }} data-testid="payment-notes-input" /></div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setPaymentDialogOpen(false)} className="rounded-none">{t('cancel')}</Button>
                <Button type="submit" className="bg-zinc-950 rounded-none" data-testid="save-payment-btn"><CreditCard size={18} className="mr-2" />{t('addPayment')}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* ====== VIEW PAYMENTS DIALOG ====== */}
        <Dialog open={viewPaymentsDialogOpen} onOpenChange={setViewPaymentsDialogOpen}>
          <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
            <DialogHeader><DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>{t('paymentHistory')}</DialogTitle></DialogHeader>
            {selectedExpense && <div className="bg-zinc-50 border border-zinc-200 p-3 mb-2"><p className="text-sm font-medium">{selectedExpense.building_name} &mdash; {selectedExpense.company} &mdash; {selectedExpense.invoice_number}</p><div className="flex justify-between text-sm mt-1"><span className="text-zinc-500">{t('invoiceAmount')}:</span><span className="font-mono font-semibold">&euro;{(selectedExpense.invoice_amount || 0).toFixed(2)}</span></div></div>}
            {loadingPayments ? <div className="py-8 text-center text-zinc-500">{t('loading')}</div> : expensePayments.length === 0 ? <div className="py-8 text-center text-zinc-400">{t('noPayments')}</div> : (
              <div className="border border-zinc-200">
                <table className="w-full text-sm">
                  <thead><tr className="bg-zinc-50 border-b border-zinc-200"><th className="text-left px-3 py-2 font-medium text-zinc-600">{t('date')}</th><th className="text-right px-3 py-2 font-medium text-zinc-600">{t('amount')}</th><th className="text-left px-3 py-2 font-medium text-zinc-600">{t('notes')}</th><th className="w-12 px-3 py-2"></th></tr></thead>
                  <tbody>
                    {expensePayments.map(p => (
                      <tr key={p.payment_id} className="border-b border-zinc-100 last:border-0" data-testid={`payment-row-${p.payment_id}`}>
                        <td className="px-3 py-2 font-mono">{p.date}</td>
                        <td className="px-3 py-2 font-mono font-semibold text-right text-emerald-700">&euro;{(p.amount || 0).toFixed(2)}</td>
                        <td className="px-3 py-2 text-zinc-600">{p.notes || <span className="text-zinc-300">&mdash;</span>}</td>
                        <td className="px-3 py-2"><button onClick={() => { setDeletingPayment(p); setDeletePaymentDialogOpen(true); }} className="p-1.5 hover:bg-red-50 text-red-500"><Trash size={16} /></button></td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot><tr className="bg-zinc-50 border-t border-zinc-200"><td className="px-3 py-2 font-semibold text-sm">{t('total')}</td><td className="px-3 py-2 font-mono font-bold text-right text-emerald-700">&euro;{expensePayments.reduce((s, p) => s + (p.amount || 0), 0).toFixed(2)}</td><td colSpan={2}></td></tr></tfoot>
                </table>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setViewPaymentsDialogOpen(false)} className="rounded-none">{t('cancel')}</Button>
              <Button onClick={() => { setViewPaymentsDialogOpen(false); if (selectedExpense) openAddPayment(selectedExpense); }} className="bg-zinc-950 rounded-none"><CreditCard size={18} className="mr-2" />{t('addPayment')}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ====== DELETE PAYMENT DIALOG ====== */}
        <AlertDialog open={deletePaymentDialogOpen} onOpenChange={setDeletePaymentDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader><AlertDialogTitle>{t('deletePayment')}</AlertDialogTitle><AlertDialogDescription>{t('confirmDeletePayment')}</AlertDialogDescription></AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-none">{t('cancel')}</AlertDialogCancel>
              <AlertDialogAction onClick={handleDeletePayment} className="bg-red-600 rounded-none" data-testid="confirm-delete-payment-btn">{t('delete')}</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* ====== DELETE ITEM DIALOG ====== */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader><AlertDialogTitle>{deleteType === 'contract' ? t('editContract') : t('deleteElevatorExpense')}</AlertDialogTitle><AlertDialogDescription>{t('confirmDelete')}</AlertDialogDescription></AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-none">{t('cancel')}</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-red-600 rounded-none" data-testid="confirm-delete-btn">{t('delete')}</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* ====== PDF STATEMENT DIALOG ====== */}
        <Dialog open={pdfDialogOpen} onOpenChange={setPdfDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader><DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>{t('pdfStatement')}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>{t('buildings')} *</Label>
                <Select value={pdfBuilding} onValueChange={setPdfBuilding} required>
                  <SelectTrigger className="mt-1 rounded-none" data-testid="pdf-building-select"><SelectValue placeholder={t('selectBuilding')} /></SelectTrigger>
                  <SelectContent>{buildings.map(b => <SelectItem key={b.building_id} value={b.building_id}>{b.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>{t('dateFrom')}</Label><DatePickerDMY value={pdfDateFrom} onChange={v => setPdfDateFrom(v)} className="mt-1 font-mono" data-testid="pdf-date-from" /></div>
                <div><Label>{t('dateTo')}</Label><DatePickerDMY value={pdfDateTo} onChange={v => setPdfDateTo(v)} className="mt-1 font-mono" data-testid="pdf-date-to" /></div>
              </div>
              <p className="text-xs text-zinc-400">{t('leaveDatesEmpty')}</p>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setPdfDialogOpen(false)} className="rounded-none">{t('cancel')}</Button>
                <Button onClick={handleExportPdf} className="bg-zinc-950 rounded-none" data-testid="export-pdf-btn"><FilePdf size={18} className="mr-2" />{t('exportPdf')}</Button>
              </DialogFooter>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
